import { useHistory, useLocation, useMutation } from '@truepill/tpos-react-router'
import type { SendBackReason } from '@truepill/tpos-types'
import {
  SendBackToFillFromPV2Reason,
  SendBackToPV1FromPV2Reason,
  PV1TriageReasons,
  TriageReasonsToExcludeFromManualAction,
} from '@truepill/tpos-types'
import { ReactComponent as CancelIcon } from 'assets/icons/cancel.svg'
import ActionButton from 'components/ActionButton'
import IconWrapper from 'components/IconWrapper'
import { SEND_BACK_TO_FILL, SEND_BACK_TO_PV1_AND_TRIAGE } from 'gql'
import useErrorToast from 'hooks/toast/useErrorToast'
import { FulfillmentQueueName } from 'hooks/useFulfillmentQueue'
import SendBackModal from 'modals/SendBackModal'
import { useModalContext } from 'providers/Overlays/ModalProvider'
import { goToFulfillment } from 'routes'
import { bodyPrimaryColor } from 'styles/styleVariables'
import type { Fill, Order } from 'types'

type SendBackButtonProps = { order: Order; fill: Fill; orderEditable: boolean }

export const SendBackButton = ({ fill, order, orderEditable }: SendBackButtonProps): JSX.Element => {
  const { showModal } = useModalContext()
  const history = useHistory()
  const { search } = useLocation()
  const showErrorToast = useErrorToast(true)

  const [sendBackToPV1AndTriage] = useMutation(SEND_BACK_TO_PV1_AND_TRIAGE)
  const [sendBackToFill] = useMutation(SEND_BACK_TO_FILL)

  const pv1RejectionReasons = Object.values(SendBackToPV1FromPV2Reason).filter(
    s => !TriageReasonsToExcludeFromManualAction.includes(s) && s !== PV1TriageReasons.PatientOutreachRequired,
  )

  return (
    <ActionButton
      disabled={!orderEditable}
      icon={
        <IconWrapper>
          <CancelIcon fill={bodyPrimaryColor} />
        </IconWrapper>
      }
      label={'Send back'}
      onClick={() => {
        showModal(() => (
          <SendBackModal
            tabs={[
              { id: 'toFill', title: 'Send to fill', options: Object.values(SendBackToFillFromPV2Reason) },
              { id: 'toPv1', title: 'Send to PV1', options: pv1RejectionReasons },
            ]}
            confirmationCallback={async (tabId: string, sendBackReason: SendBackReason, message?: string) => {
              try {
                if (tabId === 'toFill') {
                  await sendBackToFill({
                    variables: { orderId: order._id, fillId: fill._id, message, sendBackReason },
                  })
                } else if (tabId === 'toPv1') {
                  await sendBackToPV1AndTriage({
                    variables: { orderId: order._id, fillId: fill._id, message, sendBackReason },
                  })
                }
                history.push(goToFulfillment({ fulfillmentQueueName: FulfillmentQueueName.PV2, search }))
              } catch (error) {
                showErrorToast(`Fill could not be sent back: ${error.message}`)
              }
            }}
          />
        ))
      }}
    />
  )
}
